/**
 * Paperclip Cloud Protocol (pcp/1)
 *
 * Single source of truth for the wire format used by:
 *   - hub      (cloud/hub)
 *   - client   (cloud/client)
 *   - web ui   (cloud/web)
 *
 * If a field changes here, all three must update in lockstep.
 * See cloud/SPEC.md §4 for the rationale.
 */

export const PROTOCOL_VERSION = 1 as const;

export type DeviceCapabilities = {
  os: "win32" | "darwin" | "linux";
  arch: "x64" | "arm64" | string;
  cpu: { cores: number; model: string };
  memory_gb: number;
  gpu: Array<{ vendor: string; model: string; vram_gb: number }>;
  adapters: string[];
  tags: string[];
  labels: Record<string, string>;
  paperclip_version: string;
};

export type WorkStepStatus =
  | "queued"
  | "running"
  | "done"
  | "failed"
  | "cancelled"
  /**
   * Source-side terminal state for a step that was migrated mid-flight to a
   * different device. The source DB row carries `transferred_to_step_id`
   * pointing at the new (target-side) step. Distinct from "cancelled" so
   * audits and UI can tell the work is continuing elsewhere, not abandoned.
   */
  | "transferred";

export type WorkOrderStatus =
  | "draft"
  | "planning"
  | "dispatched"
  | "in_progress"
  | "complete"
  | "failed"
  | "cancelled";

export type ClientHello = {
  kind: "client";
  device_token: string;
  capabilities: DeviceCapabilities;
  client_version: string;
};

export type UiHello = {
  kind: "ui";
  session_token: string;
  client_version: string;
};

export type Welcome = {
  session_id: string;
  user_id?: string;
  device_id?: string;
  company_ids: string[];
  server_time: number;
  supported_protocols: number[];
  /**
   * Map of role → host config for the companies this device belongs to.
   * Set when this device is the designated host for one or more agent roles.
   * The client uses this to spin up the appropriate runner per role.
   * UI welcomes ignore this field.
   */
  agent_hosts?: Array<{
    role: AgentRole;
    company_id: string;
    model: string;
    permission_mode: AgentPermissionMode;
  }>;
};

export type DeviceRegister = {
  capabilities: DeviceCapabilities;
};

export type DeviceHeartbeat = {
  online: true;
  load: { cpu_pct: number; memory_pct: number };
  queue_depth: number;
  paperclip_status: "ok" | "down" | "starting";
};

export type DeviceEvent = {
  kind: "log" | "status" | "paperclip_down" | "paperclip_up";
  message?: string;
  data?: unknown;
};

/* -- Workspaces (folder list per device) ---------------------------------- */

/**
 * A folder the device has elected to expose. `path` is absolute on the
 * device's filesystem. `name` is the human-friendly label (typically the
 * basename or a configured alias). `is_git_repo` is best-effort — true if
 * a `.git` directory exists at that path. `branch` is only populated for
 * git repos and may be undefined if we couldn't read HEAD.
 */
export type Workspace = {
  path: string;
  name: string;
  is_git_repo: boolean;
  branch?: string;
  /** Mtime of the folder (ms). Useful for sorting by most-recently-touched. */
  mtime: number;
};

/**
 * Per-device snapshot of exposed folders. Reported by the client during the
 * `hello` envelope's capabilities, and re-reported in response to a
 * `device.workspaces.refresh` request. The client decides what to expose;
 * the hub never scans the device's filesystem directly.
 */
export type WorkspacesReport = {
  /** Absolute paths the client treats as "workspace roots". Folders below
   *  each root are reported in `workspaces`. */
  roots: string[];
  workspaces: Workspace[];
  /** Wall-clock at the moment of the scan, on the device. */
  scanned_at: number;
};

/**
 * Hub → client. "Refresh your workspace list and report back."
 * Triggered by the UI's "Sync" button on the Device detail page.
 */
export type DeviceWorkspacesRefresh = {
  /** RPC correlation id; the client echoes it as `ref` on its report. */
  request_id: string;
};

export type ExecDispatch = {
  work_order_id: string;
  work_step_id: string;
  agent_role: string;
  instruction: string;
  expected_outputs: string[];
  timeout_s?: number;
  context?: Record<string, unknown>;
  /**
   * Absolute path on the target device. The worker MUST `cd` to this path
   * before invoking Paperclip / claude so subprocesses inherit the right
   * cwd. Required when the user has selected a specific folder; omitted
   * means "use the worker's default cwd" (current behavior, unchanged).
   */
  working_dir?: string;
  /**
   * When set, this dispatch is a *resume* of a step that was running on a
   * different device. The target client downloads the checkpoint blob and
   * prepends it as context to the freshly-created local Paperclip issue, so
   * the agent can continue from where the source left off rather than starting
   * from zero. See SPEC §14 for the checkpoint format.
   */
  resume_from?: {
    source_work_step_id: string;
    source_device_id: string;
    checkpoint_blob_id: string | null;
    /** Inline summary (≤ 4 KB) the source produced — what was done, what's left. */
    summary?: string;
  };
};

/* -- Transfer (move a running step to a different device) ----------------- */

/**
 * Hub → source client. "Pause and checkpoint this step; we're moving it."
 * The source client cancels its local Paperclip work, gathers a checkpoint
 * (issue description + comments + work products) into a blob, and replies
 * with `exec.transfer_done`.
 */
export type ExecTransfer = {
  work_step_id: string;
  /** Where the work is going next; informational for audit logs. */
  target_device_id: string;
  reason?: string;
  /** Cap on how long the source has to checkpoint before we abort the transfer. */
  checkpoint_timeout_s?: number;
};

/**
 * Source client → hub. Reply to `exec.transfer`. On success, carries the
 * blob id of the uploaded checkpoint. On failure, carries an error code:
 *   - "no_local_state": nothing meaningful to checkpoint (work hadn't started).
 *   - "checkpoint_failed": local Paperclip query or blob upload failed.
 *   - "already_terminal": the step finished/failed before transfer landed.
 */
export type ExecTransferDone = {
  work_step_id: string;
  ok: boolean;
  checkpoint_blob_id?: string | null;
  /** Inline summary the hub will pass through to the target's resume_from. */
  summary?: string;
  error?: { code: string; message: string };
};

export type ExecUpdate = {
  work_step_id: string;
  status: WorkStepStatus;
  message?: string;
  progress_pct?: number;
};

export type ExecResult = {
  work_step_id: string;
  status: "done" | "failed" | "cancelled";
  outputs?: Array<
    | { kind: "inline"; name: string; mime: string; data: string /* base64 ≤64KB */ }
    | { kind: "blob"; name: string; mime: string; blob_id: string; bytes: number }
    | { kind: "text"; name: string; text: string }
  >;
  error?: { code: string; message: string };
  duration_ms: number;
};

export type ExecCancel = {
  work_step_id: string;
  reason?: string;
};

export type FleetSubscribe = {
  company_id: string;
};

export type FleetSnapshot = {
  company_id: string;
  devices: Array<{
    id: string;
    name: string;
    hostname: string;
    online: boolean;
    last_seen_at: number;
    capabilities: DeviceCapabilities;
    pairing_state: "active" | "pending" | "revoked";
    current_step_id: string | null;
  }>;
  active_work_orders: Array<{
    id: string;
    title: string;
    status: WorkOrderStatus;
    created_at: number;
  }>;
};

export type FleetDelta = {
  company_id: string;
  changes: Array<
    | { kind: "device.online"; device_id: string }
    | { kind: "device.offline"; device_id: string }
    | { kind: "device.added"; device: FleetSnapshot["devices"][number] }
    | { kind: "device.removed"; device_id: string }
    | { kind: "device.updated"; device_id: string; patch: Partial<FleetSnapshot["devices"][number]> }
    | { kind: "work_order.added"; work_order: FleetSnapshot["active_work_orders"][number] }
    | { kind: "work_order.updated"; work_order_id: string; patch: Partial<FleetSnapshot["active_work_orders"][number]> }
  >;
};

/* -- Cloud agents (CEO, Ops, …) ---------------------------------------- */

/**
 * Roles for cloud-hosted agents. Each role gets its own agent host designation
 * (a device that runs the local Claude CLI), its own permission mode, its own
 * conversation thread, and its own toolkit on the hub side.
 *
 * - "ceo": orchestrator. Tools: list_devices, dispatch_step, await_step.
 * - "ops": operations manager. Tools: list_devices, list_pending_work,
 *          query_history, set_priority, cancel_step, recommend_to_ceo.
 *
 * Add new roles here when you promote them to stack-level. Every layer
 * (hub, client, web) keys off this enum.
 */
export type AgentRole = "ceo" | "ops";

export const AGENT_ROLES: ReadonlyArray<AgentRole> = ["ceo", "ops"];

/**
 * Permission modes that gate tool execution per-agent. Stored on
 * `cloud_agent_hosts.permission_mode` and forwarded down to the client where
 * the Claude Agent SDK consumes it via `permissionMode` + `canUseTool`.
 *
 * The 5 modes mirror the Claude Code UI:
 *   - "ask"           : every tool call prompts the user via the web UI.
 *                       (Implemented as a canUseTool callback that round-trips
 *                       to the hub → web UI → back.)
 *   - "accept_edits"  : SDK acceptEdits — for our scoped agents that only
 *                       have non-edit MCP tools, this falls back to "ask".
 *   - "plan"          : SDK plan mode — agent describes a plan, no execution.
 *   - "auto"          : auto-accept all of this agent's tools without prompting.
 *                       For our scoped agents, equivalent to bypass.
 *   - "bypass"        : SDK bypassPermissions — no prompts at all.
 */
export type AgentPermissionMode =
  | "ask"
  | "accept_edits"
  | "plan"
  | "auto"
  | "bypass";

export const AGENT_PERMISSION_MODES: ReadonlyArray<AgentPermissionMode> = [
  "ask",
  "accept_edits",
  "plan",
  "auto",
  "bypass",
];

/**
 * UI → hub. User chats with a specific agent. `role` selects the agent;
 * the hub looks up that role's host device and forwards a turn.
 */
export type AgentMessage = {
  company_id: string;
  role: AgentRole;
  content: string;
};

/**
 * Hub → UI. Streamed assistant text from an agent turn.
 * Replaces the old per-role ceo.stream.
 */
export type AgentStream = {
  company_id: string;
  role: AgentRole;
  message_id: string;
  delta?: string;
  done?: boolean;
};

/**
 * Hub → UI. Live tool-call surfacing for whichever agent is active.
 */
export type AgentToolCall = {
  company_id: string;
  role: AgentRole;
  message_id: string;
  tool: string;
  args: unknown;
  result?: unknown;
  error?: string;
};

/**
 * Hub → agent-host client. Forwards a user message so the client can run the
 * Claude CLI turn locally for the named role.
 *
 * The host must reply with a series of `agent.stream` and `agent.tool_call`
 * envelopes, finishing with `agent.turn.complete`. While the turn is in flight,
 * it may issue any number of `agent.fleet.*_request` envelopes to the hub
 * (CEO uses dispatch tools, Ops uses query/priority tools).
 */
export type AgentMessageForward = {
  company_id: string;
  role: AgentRole;
  message_id: string;
  user_message: string;
  prior_transcript?: Array<{
    role: "user" | "agent";
    content: string;
    created_at: number;
  }>;
  /** Model id to pass to the Claude CLI (--model). */
  model: string;
  /** Permission mode the SDK should run in. */
  permission_mode: AgentPermissionMode;
  /**
   * When true, this turn is a context-compression run. The host swaps in a
   * summarization-only system prompt, disables all tools, and treats
   * `user_message` as the verbatim transcript to summarize. The reply's
   * `final_text` becomes the compression summary persisted by the hub.
   */
  compress?: boolean;
  /**
   * Absolute path on the CEO host the user picked in the SPA's folder picker.
   * The host should surface this to the LLM as additional context (e.g. in
   * the system prompt or as a pre-message) so the CEO's `dispatch_step` tool
   * call sets `working_dir` on the resulting `ExecDispatch`. Optional — when
   * omitted the worker uses its default cwd.
   */
  working_dir?: string;
};

/**
 * Agent-host client → hub. The agent has finished its turn.
 */
export type AgentTurnComplete = {
  company_id: string;
  role: AgentRole;
  message_id: string;
  final_text: string;
  end_reason: "end_turn" | "max_turns" | "error" | "cancelled";
  error?: string;
};

/* -- Permission prompt roundtrip (for "ask" mode) ------------------------- */

/**
 * Client → hub → UI. The agent's canUseTool callback fires; before the tool
 * runs, the host asks the user for permission via this envelope. The hub
 * forwards it to subscribed UIs, which show an approval prompt.
 */
export type AgentPermissionRequest = {
  company_id: string;
  role: AgentRole;
  message_id: string;
  /** Stable id for this prompt; referenced by AgentPermissionResponse.request_id. */
  request_id: string;
  tool: string;
  args: unknown;
  /** Soft hint to the UI: "low" | "medium" | "high" risk. */
  risk: "low" | "medium" | "high";
};

/**
 * UI → hub → client. The user's decision on a pending tool call.
 */
export type AgentPermissionResponse = {
  company_id: string;
  request_id: string;
  decision: "allow_once" | "allow_session" | "deny";
  /** Optional override of the args (e.g., user edited the instruction before approving). */
  updated_args?: unknown;
  message?: string;
};

/* -- Generic agent ↔ hub RPCs (request/response pairs by envelope.ref) ---- */

/** Used by both CEO and Ops. */
export type AgentFleetListRequest = {
  company_id: string;
  role: AgentRole;
};

export type AgentFleetListResponse = {
  devices: FleetSnapshot["devices"];
  error?: string;
};

/** CEO-only in v1. The hub rejects with `{error:"role_not_permitted"}` if Ops calls it. */
export type AgentFleetDispatchRequest = {
  company_id: string;
  role: AgentRole;
  message_id: string;
  device_id: string;
  agent_role: string;
  instruction: string;
  expected_outputs: string[];
  timeout_s?: number;
  priority?: number;
  /**
   * Optional absolute path on the target device. When set, the hub forwards
   * it on the resulting `exec.dispatch` envelope so the worker `cd`s into
   * that folder before invoking Paperclip. See ExecDispatch.working_dir.
   */
  working_dir?: string;
};

export type AgentFleetDispatchResponse = {
  work_step_id?: string;
  work_order_id?: string;
  error?: string;
};

/** CEO-only. */
export type AgentFleetAwaitRequest = {
  company_id: string;
  role: AgentRole;
  work_step_id: string;
  timeout_s?: number;
};

export type AgentFleetAwaitResponse = {
  status?: "done" | "failed" | "cancelled";
  outputs?: ExecResult["outputs"];
  step_error?: { code: string; message: string };
  duration_ms?: number;
  error?: string;
};

/* -- Ops-specific RPCs ---------------------------------------------------- */

/** Ops: list pending and running work, with timing context. */
export type AgentOpsListWorkRequest = {
  company_id: string;
  role: AgentRole;
  /** Filter by status: 'pending' (queued) | 'running' | 'all'. Default 'all'. */
  filter?: "pending" | "running" | "all";
};

export type AgentOpsListWorkResponse = {
  orders?: Array<{
    id: string;
    title: string;
    status: WorkOrderStatus;
    priority: number;
    created_at: number;
    requested_by_user_id: string | null;
    steps: Array<{
      id: string;
      device_id: string | null;
      agent_role: string;
      status: WorkStepStatus;
      started_at: number | null;
      queued_for_ms: number;
      running_for_ms: number;
    }>;
  }>;
  error?: string;
};

/** Ops: query historical step outcomes with timing stats. */
export type AgentOpsHistoryRequest = {
  company_id: string;
  role: AgentRole;
  hours_back: number;
  agent_role?: string;
  device_id?: string;
};

export type AgentOpsHistoryResponse = {
  steps?: Array<{
    id: string;
    work_order_id: string;
    device_id: string | null;
    agent_role: string;
    status: WorkStepStatus;
    duration_ms: number;
    ended_at: number | null;
  }>;
  /** Aggregated stats over the returned steps. */
  stats?: {
    total: number;
    by_status: Record<WorkStepStatus, number>;
    avg_duration_ms_by_role: Record<string, number>;
  };
  error?: string;
};

/** Ops: change a work order's priority (higher = sooner). */
export type AgentOpsSetPriorityRequest = {
  company_id: string;
  role: AgentRole;
  work_order_id: string;
  priority: number;
  reason?: string;
};

export type AgentOpsSetPriorityResponse = {
  ok?: true;
  error?: string;
};

/** Ops: cancel a running or queued step. */
export type AgentOpsCancelStepRequest = {
  company_id: string;
  role: AgentRole;
  work_step_id: string;
  reason: string;
};

export type AgentOpsCancelStepResponse = {
  ok?: true;
  error?: string;
};

/** Ops: leave a recommendation in the CEO's chat thread. */
export type AgentOpsRecommendRequest = {
  company_id: string;
  role: AgentRole;
  recommendation: string;
};

export type AgentOpsRecommendResponse = {
  ok?: true;
  error?: string;
};

/** Ops: move a running step to a different device. */
export type AgentOpsTransferStepRequest = {
  company_id: string;
  role: AgentRole;
  work_step_id: string;
  target_device_id: string;
  reason?: string;
};

export type AgentOpsTransferStepResponse = {
  source_work_step_id?: string;
  new_work_step_id?: string;
  checkpoint_blob_id?: string | null;
  error?: string;
};

export type WorkOrderUpdate = {
  work_order_id: string;
  status: WorkOrderStatus;
  steps?: Array<{
    id: string;
    device_id: string | null;
    agent_role: string;
    status: WorkStepStatus;
    started_at: number | null;
    ended_at: number | null;
  }>;
};

export type ErrorBody = {
  code: string;
  message: string;
  retriable: boolean;
};

export type MessageType =
  | "hello"
  | "welcome"
  | "ping"
  | "pong"
  | "device.register"
  | "device.heartbeat"
  | "device.event"
  | "exec.dispatch"
  | "exec.update"
  | "exec.result"
  | "exec.cancel"
  | "exec.transfer"
  | "exec.transfer_done"
  | "fleet.subscribe"
  | "fleet.snapshot"
  | "fleet.delta"
  // Per-device workspace folder list (see Workspace / WorkspacesReport above).
  | "device.workspaces.refresh"
  | "device.workspaces.report"
  // Generic agent envelopes (replace the old per-role ceo.* set).
  | "agent.message"
  | "agent.stream"
  | "agent.tool_call"
  | "agent.message.forward"
  | "agent.turn.complete"
  | "agent.permission.request"
  | "agent.permission.response"
  | "agent.fleet.list_request"
  | "agent.fleet.list_response"
  | "agent.fleet.dispatch_request"
  | "agent.fleet.dispatch_response"
  | "agent.fleet.await_request"
  | "agent.fleet.await_response"
  // Ops-specific RPCs (Ops only; CEO requests are rejected).
  | "agent.ops.list_work_request"
  | "agent.ops.list_work_response"
  | "agent.ops.history_request"
  | "agent.ops.history_response"
  | "agent.ops.set_priority_request"
  | "agent.ops.set_priority_response"
  | "agent.ops.cancel_step_request"
  | "agent.ops.cancel_step_response"
  | "agent.ops.recommend_request"
  | "agent.ops.recommend_response"
  | "agent.ops.transfer_step_request"
  | "agent.ops.transfer_step_response"
  | "work_order.update"
  | "error";

export type BodyByType = {
  hello: ClientHello | UiHello;
  welcome: Welcome;
  ping: Record<string, never>;
  pong: Record<string, never>;
  "device.register": DeviceRegister;
  "device.heartbeat": DeviceHeartbeat;
  "device.event": DeviceEvent;
  "exec.dispatch": ExecDispatch;
  "exec.update": ExecUpdate;
  "exec.result": ExecResult;
  "exec.cancel": ExecCancel;
  "exec.transfer": ExecTransfer;
  "exec.transfer_done": ExecTransferDone;
  "fleet.subscribe": FleetSubscribe;
  "fleet.snapshot": FleetSnapshot;
  "fleet.delta": FleetDelta;
  "device.workspaces.refresh": DeviceWorkspacesRefresh;
  "device.workspaces.report": WorkspacesReport;
  "agent.message": AgentMessage;
  "agent.stream": AgentStream;
  "agent.tool_call": AgentToolCall;
  "agent.message.forward": AgentMessageForward;
  "agent.turn.complete": AgentTurnComplete;
  "agent.permission.request": AgentPermissionRequest;
  "agent.permission.response": AgentPermissionResponse;
  "agent.fleet.list_request": AgentFleetListRequest;
  "agent.fleet.list_response": AgentFleetListResponse;
  "agent.fleet.dispatch_request": AgentFleetDispatchRequest;
  "agent.fleet.dispatch_response": AgentFleetDispatchResponse;
  "agent.fleet.await_request": AgentFleetAwaitRequest;
  "agent.fleet.await_response": AgentFleetAwaitResponse;
  "agent.ops.list_work_request": AgentOpsListWorkRequest;
  "agent.ops.list_work_response": AgentOpsListWorkResponse;
  "agent.ops.history_request": AgentOpsHistoryRequest;
  "agent.ops.history_response": AgentOpsHistoryResponse;
  "agent.ops.set_priority_request": AgentOpsSetPriorityRequest;
  "agent.ops.set_priority_response": AgentOpsSetPriorityResponse;
  "agent.ops.cancel_step_request": AgentOpsCancelStepRequest;
  "agent.ops.cancel_step_response": AgentOpsCancelStepResponse;
  "agent.ops.recommend_request": AgentOpsRecommendRequest;
  "agent.ops.recommend_response": AgentOpsRecommendResponse;
  "agent.ops.transfer_step_request": AgentOpsTransferStepRequest;
  "agent.ops.transfer_step_response": AgentOpsTransferStepResponse;
  "work_order.update": WorkOrderUpdate;
  error: ErrorBody;
};

export type Envelope<T extends MessageType = MessageType> = {
  v: typeof PROTOCOL_VERSION;
  t: T;
  id: string;
  ref?: string;
  device_id?: string;
  company_id?: string;
  ts: number;
  body: BodyByType[T];
};

/**
 * ulid-ish id generator. Lexicographically sortable: 10-char timestamp + 16-char random.
 * Avoids pulling a dep into a file shared by 3 packages.
 */
export function newId(): string {
  const t = Date.now().toString(36).padStart(10, "0");
  const r = Array.from({ length: 16 }, () =>
    Math.floor(Math.random() * 36).toString(36),
  ).join("");
  return `${t}${r}`;
}

export function envelope<T extends MessageType>(
  t: T,
  body: BodyByType[T],
  opts: { id?: string; ref?: string; device_id?: string; company_id?: string } = {},
): Envelope<T> {
  return {
    v: PROTOCOL_VERSION,
    t,
    id: opts.id ?? newId(),
    ref: opts.ref,
    device_id: opts.device_id,
    company_id: opts.company_id,
    ts: Date.now(),
    body,
  };
}

export const HEARTBEAT_INTERVAL_MS = 25_000;
export const HEARTBEAT_TIMEOUT_MS = 60_000;
export const MAX_INLINE_OUTPUT_BYTES = 64 * 1024;
export const MAX_CLOCK_SKEW_MS = 5 * 60_000;
